import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TextInputKeyPressEvent } from "react-native";
import type { View } from "react-native";
import { useClickOutside } from "./use-click-outside";
import { useListKeyboard } from "./use-list-keyboard";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseComboboxOptions<T> {
  /** The list of options currently available in the dropdown. */
  readonly options: readonly T[];
  /** Stable identifier for each option (used for highlight reset detection). */
  readonly getOptionId: (option: T) => string;
  /** Called when an option is selected (Enter key or press). */
  readonly onSelect?: ((option: T) => void) | undefined;
  /** Controlled open state — omit for uncontrolled. */
  readonly isOpen?: boolean | undefined;
  /** Called when the open state changes (for controlled mode or side-effects). */
  readonly onOpenChange?: ((open: boolean) => void) | undefined;
  /** If true, the dropdown stays open after selecting an option. */
  readonly disableCloseOnSelect?: boolean | undefined;
  /** If true, keyboard navigation does not wrap from last to first. */
  readonly disableListWrap?: boolean | undefined;
  /** Called when the user presses Enter with no highlighted option. */
  readonly onSubmit?: ((inputValue: string) => void) | undefined;
  /** Current text input value (needed for onSubmit fallback). */
  readonly inputValue?: string | undefined;
}

export interface ComboboxOptionProps {
  readonly focused: boolean;
  readonly onPress: () => void;
  readonly onHoverIn: () => void;
}

export interface UseComboboxReturn<T> {
  /** Whether the dropdown is currently open. */
  readonly isOpen: boolean;
  /** Open the dropdown. */
  readonly open: () => void;
  /** Close the dropdown. */
  readonly close: () => void;
  /** Index of the currently highlighted option (-1 = none). */
  readonly highlightedIndex: number;
  /** Manually set the highlighted index. */
  readonly setHighlightedIndex: (index: number) => void;
  /** Reset highlight to -1. */
  readonly resetHighlight: () => void;
  /** Ref to attach to the container View (for click-away detection). */
  readonly containerRef: React.RefObject<View | null>;
  /** Key press handler to attach to the TextInput. */
  readonly handleKeyPress: (e: TextInputKeyPressEvent) => void;
  /** Called when the input receives focus (opens the dropdown). */
  readonly handleInputFocus: () => void;
  /** Get props to spread on an individual option element. */
  readonly getOptionProps: (option: T, index: number) => ComboboxOptionProps;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useCombobox<T>(config: UseComboboxOptions<T>): UseComboboxReturn<T> {
  const {
    options: items,
    getOptionId,
    onSelect,
    isOpen: controlledOpen,
    onOpenChange,
    disableCloseOnSelect = false,
    disableListWrap = false,
    onSubmit,
    inputValue = "",
  } = config;

  // --- Open state (controlled or uncontrolled) ---
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setInternalOpen(next);
      }
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const open = useCallback(() => setOpen(true), [setOpen]);
  const close = useCallback(() => setOpen(false), [setOpen]);

  // --- Selection ---
  const handleSelect = useCallback(
    (option: T) => {
      onSelect?.(option);
      if (!disableCloseOnSelect) {
        close();
      }
    },
    [onSelect, disableCloseOnSelect, close],
  );

  const handleHighlightSelect = useCallback(
    (index: number) => {
      const item = items[index];
      if (item) {
        handleSelect(item);
      }
    },
    [items, handleSelect],
  );

  // --- Keyboard navigation (delegated to useListKeyboard) ---
  const { highlightedIndex, setHighlightedIndex, resetHighlight, handleKeyPress } = useListKeyboard({
    totalItems: items.length,
    isOpen,
    open,
    close,
    onHighlightSelect: handleHighlightSelect,
    onSubmit,
    inputValue,
    disableListWrap,
  });

  // Reset highlight when options identity changes
  const optionKey = useMemo(() => items.map(getOptionId).join("\0"), [items, getOptionId]);
  const prevOptionKeyRef = useRef(optionKey);
  useEffect(() => {
    if (prevOptionKeyRef.current !== optionKey) {
      prevOptionKeyRef.current = optionKey;
      resetHighlight();
    }
  }, [optionKey, resetHighlight]);

  // --- Input focus ---
  const handleInputFocus = useCallback(() => {
    open();
  }, [open]);

  // --- Click-outside (web only; native uses backdrop Pressable) ---
  const containerRef = useRef<View>(null);

  useClickOutside(containerRef, close, isOpen);

  // --- Option props getter ---
  const getOptionProps = useCallback(
    (_option: T, index: number): ComboboxOptionProps => ({
      focused: index === highlightedIndex,
      onPress: () => handleSelect(items[index] as T),
      onHoverIn: () => setHighlightedIndex(index),
    }),
    [highlightedIndex, handleSelect, items, setHighlightedIndex],
  );

  return {
    isOpen,
    open,
    close,
    highlightedIndex,
    setHighlightedIndex,
    resetHighlight,
    containerRef,
    handleKeyPress,
    handleInputFocus,
    getOptionProps,
  };
}
